import { useCallback, useEffect, useState } from "react";
import type { SimilarVenue } from "../../types/venues.types";

interface UseSimilarVenuesProps {
  venueId: string | undefined;
  apiService: {
    get: <T>(url: string) => Promise<{ data: T }>;
  };
}

interface SimilarVenuesResponse {
  similar_venues: SimilarVenue[];
}

interface UseSimilarVenuesReturn {
  similarVenues: SimilarVenue[];
  loading: boolean;
  refetch: () => void;
}

export const useSimilarVenues = ({
  venueId,
  apiService,
}: UseSimilarVenuesProps): UseSimilarVenuesReturn => {
  const [similarVenues, setSimilarVenues] = useState<SimilarVenue[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSimilarVenues = useCallback(async () => {
    if (!venueId) return;
    try {
      setLoading(true);
      const response = await apiService.get<SimilarVenuesResponse>(
        `/api/venues/venue/${venueId}/similar-venues/`,
      );
      setSimilarVenues(response.data.similar_venues || []);
    } catch (err) {
      console.error("Failed to load similar venues:", err);
      setSimilarVenues([]);
    } finally {
      setLoading(false);
    }
  }, [venueId, apiService]);

  useEffect(() => {
    fetchSimilarVenues();
  }, [fetchSimilarVenues]);

  return {
    similarVenues,
    loading,
    refetch: fetchSimilarVenues,
  };
};

export default useSimilarVenues;
